import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AppsIcon from '@mui/icons-material/Apps';
import BadgeIcon from '@mui/icons-material/Badge';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import WebhookIcon from '@mui/icons-material/Webhook';
import { alpha, Theme } from '@mui/material/styles';
import { createOrgScope, permission } from '@nohara-dxdev/oidc-core';
import {
  BreadcrumbItem,
  DashboardLayout,
  DashboardNavMenuItem,
  useIsOrgAdmin,
  useIsSystemAdmin,
  useOidc,
  useScopeIds,
  useUserOrgs,
  useOrg,
} from '@nohara-dxdev/react-oidc';
import { PropsWithChildren, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { env } from '@/config/env';
import { paths } from '@/config/paths';

const permissions = {
  readApp: [permission.app.read],
  readRole: [permission.role.read],
  readUser: [permission.user.read],
  readOrg: [permission.org.read],
  readWebhook: [permission.webhook.read],
  readEmailTemplate: [permission.emailTemplate.read],
};

export const OrgLayout = ({ children }: PropsWithChildren) => {
  const { userId } = useOidc();
  const { t } = useTranslation();
  const { data: orgs } = useUserOrgs(userId);
  const context = useScopeIds();
  const { orgId } = context;
  const { data: org } = useOrg(orgId);

  const isSystemAdmin = useIsSystemAdmin();
  const isOrgAdmin = useIsOrgAdmin(orgId);

  const hasMoreThanOneOrg = !!orgs?.length && orgs.length > 1;
  const orgScope = useMemo(() => createOrgScope(orgId), [orgId]);

  const sections = useMemo((): DashboardNavMenuItem[][] => {
    const mainSection: DashboardNavMenuItem[] = [];
    if (orgId) {
      mainSection.push(
        {
          path: paths.org.dashboard.getHref(orgId),
          icon: <DashboardIcon />,
          label: t('navigation.menu.dashboard'),
          context,
        },
        {
          path: paths.org.apps.getHref(orgId),
          icon: <AppsIcon />,
          label: t('navigation.menu.apps'),
          requireAll: permissions.readApp,
          scope: orgScope,
          context,
        },
        {
          path: paths.org.roles.getHref(orgId),
          icon: <BadgeIcon />,
          label: t('navigation.menu.roles'),
          requireAll: permissions.readRole,
          scope: orgScope,
          context,
        },
        {
          path: paths.org.users.getHref(orgId),
          icon: <PeopleIcon />,
          label: t('navigation.menu.users'),
          requireAll: permissions.readUser,
          scope: orgScope,
          context,
        },
        ...(isOrgAdmin
          ? [
              {
                path: paths.org.webhooks.getHref(orgId),
                icon: <WebhookIcon />,
                label: t('navigation.menu.webhooks'),
                requireAll: permissions.readWebhook,
                scope: orgScope,
                context,
              },
            ]
          : []),
        {
          path: paths.org.emailTemplate.getHref(orgId),
          icon: <EmailIcon />,
          label: t('navigation.menu.emailTemplate'),
          requireAll: permissions.readEmailTemplate,
          scope: orgScope,
          context,
        },
        {
          path: paths.org.orgProfile.getHref(orgId),
          icon: <SettingsIcon />,
          label: t('navigation.menu.orgProfile'),
          requireAll: permissions.readOrg,
          scope: orgScope,
          context,
        },
      );
    }

    return mainSection.length > 0 ? [mainSection] : [];
  }, [isOrgAdmin, orgId, orgScope, t, context]);

  const bottomSections = useMemo((): DashboardNavMenuItem[][] => {
    const items: DashboardNavMenuItem[] = [];
    if (hasMoreThanOneOrg) {
      items.push({
        path: paths.org.root.getHref(),
        icon: <HomeIcon />,
        label: t('navigation.menu.home'),
      });
    }
    if (isSystemAdmin) {
      items.push({
        path: paths.admin.root.path,
        icon: <AdminPanelSettingsIcon />,
        label: t('navigation.menu.adminPanel'),
      });
    }
    return items.length > 0 ? [items] : [];
  }, [hasMoreThanOneOrg, isSystemAdmin, t]);

  const breadcrumbs: BreadcrumbItem[] = useMemo(() => {
    const crumbs: BreadcrumbItem[] = [];
    if (orgId) {
      crumbs.push({
        label: org?.name ?? t('navigation.orgTitle'),
        href: paths.org.dashboard.getHref(orgId),
      });
    }
    return crumbs;
  }, [orgId, org?.name, t]);

  return (
    <DashboardLayout
      sections={sections}
      bottomSections={bottomSections}
      navTitle={org?.name ?? t('navigation.orgTitle')}
      pageTitle={org?.name ?? t('navigation.orgAppTitle')}
      breadcrumbs={breadcrumbs}
      paths={{
        logout: paths.auth.login.path,
        userProfile: paths.org.userProfile.getHref(orgId),
      }}
      appBarSx={{
        bgcolor: 'info.dark',
      }}
      drawerSx={{
        '& .MuiDrawer-paper': {
          bgcolor: (theme: Theme) => alpha(theme.palette.info.dark, 0.05),
        },
      }}
      appVersion={`${t('common.appVersion')}: ${env.APP_VERSION}`}
      backendVersion={`${t('common.backendVersion')}: ${env.BACKEND_VERSION}`}
    >
      {children}
    </DashboardLayout>
  );
};
